<template>
  <div>
    <!-- {{ kObjects }} -->
    <v-card class="blueCard mb-6" flat :loading="loading || loadingRx">
      <v-card-title class="d-flex flex-row flex-wrap py-1">
        <div style="width: 80px" class="mr-3">
          <v-img
            contain
            width="70"
            src="https://www.eyedock.com/images/pathologies/search.svg"
          ></v-img>
        </div>
        <div class="text-h5">Search Everything</div> </v-card-title
      ><v-card-text>
        <div v-if="q" class="d-flex flex-row align-start">
          <div class="font-weight-medium">Your Query:</div>
          <div class="d-flex flex-row align-end">
            <div class="caption mx-2">
              <span v-if="expandQuery">{{ q }}</span
              ><span v-else>{{ q | truncate(100) }}</span>
            </div>
            <v-btn
              icon
              small
              color="primary"
              v-if="showQueryIcon"
              @click="expandQuery = !expandQuery"
              class="ml-2"
              ><v-icon small color="primary">mdi-dots-horizontal</v-icon></v-btn
            >
            <v-btn
              v-if="!editMode"
              text
              color="primary"
              x-small
              @click="editMode = true"
              class="ml-2"
              ><v-icon x-small color="primary" class="mr-2">mdi-pencil</v-icon>
              Edit</v-btn
            >
            <v-btn
              v-if="editMode"
              text
              color="primary"
              x-small
              @click="editMode = false"
              class="ml-2"
              ><v-icon x-small color="primary" class="mr-2">mdi-pencil</v-icon>
              Cancel</v-btn
            >
          </div>
        </div>
      </v-card-text>
    </v-card>

    <div v-if="!q || editMode" class="pa-2">
      <v-fade-transition>
        <v-alert
          v-if="tooFewChars && (!qInput || qInput.length < 3)"
          type="warning"
          >Aw, that's too short. Give me at least 3 letters!</v-alert
        >
      </v-fade-transition>

      <v-textarea
        v-model="qInput"
        outlined
        placeholder="Type or paste something to search for"
        clearable
      ></v-textarea>
      <div class="d-flex flex-row">
        <v-spacer></v-spacer>
        <v-btn
          @click="editMode = false"
          text
          color="primary"
          small
          tabindex="-1"
          >Cancel</v-btn
        >
        <v-btn
          @click="newQ()"
          depressed
          small
          color="primary"
          class=""
          :disabled="!qInput || (tooFewChars && qInput.length < 3)"
          >Search</v-btn
        >
      </div>
    </div>
    <div v-else>
      <v-alert
        v-if="
          q &&
          !((itemsToShow && itemsToShow.length) || hasCalculatables) &&
          !loading &&
          !loadingRx &&
          !loadingK
        "
        class="mx-3 my-3"
        :border="'left'"
        colored-border
        type="warning"
        elevation="1"
        >Sorry, I did not finding anything in EyeDock's databases matching your
        query.
        <p>Let me know exactly what you're looking for and I'll try again.</p>

        <ul>
          <li>
            <a :href="`/cls/search?q=${uriEncoded}`"
              >I'm looking for contact lenses</a
            >
          </li>
          <li>
            <a :href="`/meds/${uriEncoded}`">I'm looking for a medication</a>
          </li>
          <li v-if="isSubscribed">
            <a :href="`/icd10/search/${uriEncoded}`"
              >I'm looking for ICD10 Codes</a
            >
          </li>
          <li v-if="isSubscribed">
            <a :href="`/ocular-conditions/search/${uriEncoded}`"
              >I'm looking for an Ocular Disease/Condition</a
            >
          </li>
        </ul>
      </v-alert>
      <!-- outer container if there IS a query -->
      <div class="">
        <v-card
          v-if="hasRefraction"
          :loading="loadingRx"
          class="my-2 blueCard"
          flat
        >
          <v-card-title class="pb-0 pt-1">
            <!-- <div class="overline"> -->
            <div class="d-inline-block mr-6">Optical Rx's</div>

            <rx-parser-alternatives
              v-if="rxAlternatives"
              class="d-inline-block"
              :rxAlternatives="rxAlternatives"
              :rxText="rxText"
              @newText="
                qInput = rxText = $event;
                doPowerSearch();
              "
              @newRxs="rxObjects = $event"
            ></rx-parser-alternatives>
            <!-- </div> -->
          </v-card-title>
          <!-- <v-divider class="my-2"></v-divider> -->

          <rx-summary-card-pair
            v-if="rxObjects"
            :rxs="rxObjects"
            :showCrosses="$vuetify.breakpoint.smAndUp"
            :dontVertex="false"
            :center="false"
            minCardWidth="415"
            cardColor="transparent"
            style="min-height: 130px"
          >
          </rx-summary-card-pair>
        </v-card>
        <v-card
          v-if="hasKeratometry"
          :loading="loadingK"
          class="my-2 blueCard"
          flat
        >
          <v-card-title class="pb-0 pt-1">
            <div class="d-inline-block mr-6">Keratometry</div>
            <k-parser-alternatives
              v-if="kAlternatives"
              class="d-inline-block"
              :kAlternatives="kAlternatives"
              :kText="kText"
              @newText="
                kText = $event;
                doKeratoSearch();
              "
              @newKs="kObjects = $event"
            ></k-parser-alternatives>
          </v-card-title>

          <k-summary-card-pair
            :kObjects="kObjects"
            :showCrosses="$vuetify.breakpoint.smAndUp"
            :center="false"
            minCardWidth="415"
            style="min-height: 130px"
            cardColor="transparent"
          >
          </k-summary-card-pair>
        </v-card>
      </div>
      <div class="mx-2 mt-6" v-if="hasCalculatables">
        <div class="title">With the above . . .</div>
        <div class="caption red--text" v-if="!isSubscribed">
          (Rx &amp; Keratometry actions require EyeDock Pro)
        </div>
      </div>
      <div class="d-flex flex-row flex-wrap mb-6">
        <div
          v-for="(group, i) in rxActionItems"
          :key="'rxSection' + i"
          style="max-width: 100%"
        >
          <v-list
            v-if="
              (hasKeratometry && group.needs.includes('k')) ||
              (hasRefraction && group.needs.includes('rx'))
            "
            dense
            nav
            :width="$vuetify.breakpoint.smAndUp ? 370 : '100%'"
          >
            <v-list-item-group>
              <v-subheader class="overline">{{ group.section }}</v-subheader>
              <v-list-item
                class="listItem"
                v-for="(item, n) in group.items"
                :key="'rxItem' + n"
                :to="{ path: item.url + rxActions[item.hash] }"
                :disabled="!isSubscribed"
              >
                <v-list-item-icon>
                  <v-img contain :src="item.img" width="55" height="35"></v-img>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title v-html="item.title"></v-list-item-title>
                  <v-list-item-subtitle
                    v-html="item.subtitle"
                  ></v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </div>
      </div>

      <v-data-table
        v-if="itemsToShow && itemsToShow.length"
        :items="itemsToShow"
        :headers="headersForSize"
        group-by="module"
        :loading="loading"
        :items-per-page="100"
        dense
        class="pointer daTable"
        @click:row="rowClick"
        :hide-default-header="!$vuetify.breakpoint.smAndUp"
      >
        <template v-slot:group.header="{ items }">
          <td :colspan="headers.length">
            <div class="title d-flex flex-row py-2">
              <!-- <icon-or-image
                :data="iconForModuleItem({ module: items[0].module })"
                :imageWidth="25"
                class="pr-2"
              ></icon-or-image> -->
              {{ moduleOptions[items[0].module].name }}
            </div>
          </td>
        </template>

        <template v-slot:item.image="{ item }">
          <v-img
            v-if="item.image"
            :src="fullPathForProductImage(item.module, item.type, item.image)"
            contain
            :max-width="70"
            :max-height="50"
            position="center center"
            class="ma-1"
          ></v-img>
          <div v-else-if="item.type == 'COMPONENT' || item.type == 'COMPANY'">
            <icon-or-image
              :data="iconForModuleItem(item)"
              :imageWidth="20"
            ></icon-or-image>
          </div>
          <div
            v-else-if="item.module == 'ICD10'"
            class="overline primary--text"
          >
            {{ item.identifier }}
          </div>
          <div v-else-if="item.module == 'BC'" class="overline primary--text">
            {{ item.groups.category }}
          </div>
          <div v-else class="overline green--text">{{ item.module }}</div>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ item.name }}</div>
          <div class="caption" v-if="item.subtitle">{{ item.subtitle }}</div>
          <div
            v-if="!item.subtitle && item.type == 'COMPONENT'"
            class="caption"
          >
            <span v-if="item.module == 'CL'"
              >Lenses with polymer {{ item.name }}</span
            >
            <span v-if="item.module == 'MED'"
              >Meds including {{ item.name }}</span
            >
          </div>
          <div v-if="item.discontinued == '1'" class="caption red--text">
            (discontinued)
          </div>
        </template>

        <template v-slot:item.module="{ item }">
          <icon-or-image
            :data="iconForModuleItem(item)"
            :imageWidth="25"
          ></icon-or-image>
        </template>

        <template v-slot:item.groups="{ item }">
          <div
            v-if="item.type == 'PRODUCT' || item.type == 'PRIVATE'"
            class="d-flex flex-row"
          >
            <med-icon-block
              v-if="item.module == 'MED'"
              :listing="item.groups"
              :row="true"
            ></med-icon-block>
            <soft-type-icon
              v-if="item.module == 'CL'"
              :lens="{ type_tag: item.groups.type }"
              :width="20"
              class="mr-2"
            ></soft-type-icon>
            <soft-type-icon
              v-if="item.module == 'CL'"
              :lens="item.groups"
              type="replace"
              :width="20"
              class="mr-2"
            ></soft-type-icon>
          </div>
          <div
            v-if="item.groups && item.groups.category && item.module != 'BC'"
            class="caption"
          >
            {{ item.groups.category | titleize }}
          </div>
          <div
            v-if="item.groups && item.groups.type && item.module != 'CL'"
            class="caption"
          >
            {{ item.groups.type | titleize }}
          </div>
        </template>

        <template v-slot:item.unoHeader="{ item }">
          <div class="d-flex flex-row my-1 align-center">
            <div class="mobileImageDiv pr-2">
              <!-- {{ fullPathForProductImage(item.module, item.type, item.image) }} -->
              <v-img
                v-if="item.image"
                :src="
                  fullPathForProductImage(item.module, item.type, item.image)
                "
                contain
                :max-width="70"
                :max-height="50"
                position="center center"
                class="ma-1"
              ></v-img>
              <icon-or-image
                v-else
                :data="iconForModuleItem(item)"
                :imageWidth="20"
              ></icon-or-image>
            </div>
            <v-divider vertical></v-divider>
            <div class="text-left ml-2">
              <div>
                <span class="font-weight-medium">{{ item.name }}</span>
                <span v-if="item.module == 'ICD10'" class="ml-2">
                  ({{ item.identifier }})
                </span>

                <div>
                  <div class="caption" v-if="item.subtitle">
                    {{ item.subtitle }}
                  </div>
                  <div
                    v-if="item.type == 'PRODUCT' || item.type == 'PRIVATE'"
                    class="d-flex flex-row"
                  >
                    <med-icon-block
                      v-if="item.module == 'MED'"
                      :listing="item.groups"
                      :row="true"
                    ></med-icon-block>
                    <soft-type-icon
                      v-if="item.module == 'CL'"
                      :lens="{ type_tag: item.groups.type }"
                      :width="20"
                      class="mr-2"
                    ></soft-type-icon>
                    <soft-type-icon
                      v-if="item.module == 'CL'"
                      :lens="item.groups"
                      type="replace"
                      :width="20"
                      class="mr-2"
                    ></soft-type-icon>
                  </div>
                  <div
                    v-if="
                      item.groups && item.groups.category && item.module != 'BC'
                    "
                    class="caption"
                  >
                    {{ item.groups.category | titleize }}
                  </div>
                  <div
                    v-if="
                      item.groups && item.groups.type && item.module != 'CL'
                    "
                    class="caption"
                  >
                    {{ item.groups.type | titleize }}
                  </div>
                </div>
                <div v-if="item.discontinued == '1'" class="caption red--text">
                  (discontinued)
                </div>
              </div>
            </div>
          </div>
        </template>
      </v-data-table>
    </div>
    <v-alert
      v-if="!isSubscribed"
      type="info"
      class="mx-3 my-4"
      :border="'left'"
      colored-border
      style="background-color: #f7fbff; border: 1px solid #adcde7"
      bordered
    >
      <div class="title">Did you know?</div>
      EyeDock <b>Pro</b> users can search for:
      <ul>
        <li>Contact lenses by refraction</li>
        <li>ICD10 and billing codes</li>
        <li>Ocular conditions</li>
        <li>Calculate GP and hybrid lenses</li>
        <li>and so, so much more</li>
      </ul>
      <a href="documentation/about-eyedock-pro">Learn More about EyeDock Pro</a>
      <div class="my-2">
        <div class="">Already a member of EyeDock Pro?</div>
        <user-login :textButton="false" buttonLabel="Login"></user-login>
      </div>
    </v-alert>
  </div>
</template>

<script>
// import Name from "../dir/file";
// import axios from "axios";
import RxMethods from "@/assets/js/cls/RxMethods";
import ApiServices from "@/services/ApiServices";
import ModuleAndIcons from "@/assets/js/ModuleAndIconsMixin";
import UserMixin from "@/assets/js/UserMixin";

export default {
  name: "SearchAllHome",
  components: {
    RxSummaryCardPair: () => import("@/components/clStuff/RxSummaryCardPair"),
    KSummaryCardPair: () => import("@/components/clStuff/KSummaryCardPair"),
    IconOrImage: () => import("@/components/universalStuff/IconOrImage"),
    MedIconBlock: () => import("@/components/medStuff/MedIconBlock.vue"),
    SoftTypeIcon: () => import("@/components/clStuff/SoftTypeIcon.vue"),
    UserLogin: () => import("@/components/universalStuff/UserLogin"),
    RxParserAlternatives: () =>
      import("@/components/clStuff/RxParserAlternatives"),
    KParserAlternatives: () =>
      import("@/components/clStuff/KParserAlternatives"),
  },
  mixins: [RxMethods, ModuleAndIcons, UserMixin],

  props: {},
  data: function () {
    return {
      q: null,
      kText: null,
      rxText: null,
      editMode: false,
      qInput: null,
      expandQuery: false,
      loadingRx: false,
      loadingK: false,
      loading: false,
      items: null,
      groups: null,
      tooFewChars: null,
      //   rxResult: null,
      rxObjects: { od: null, os: null },
      kObjects: { od: null, os: null },
      rxAlternatives: null,
      kAlternatives: null,
      // nonSubscriberModules: ["CL", "MED", "ORAL", "IMG"],
      deepSearch: { CL: true },
      headers: [
        { text: "Module", value: "module" },
        { name: "Image", value: "image", align: "center", width: "60" },
        { text: "Name", value: "name", width: "350" },
        { text: "Categories", value: "groups" },
      ],
      unoHeader: [
        { text: "Module", value: "module" },
        { text: "Name", value: "unoHeader" },
      ],
      // todo - put the following in another file ..?
      rxActionItems: [
        {
          section: "Find Soft Lenses",
          needs: ["rx"],
          items: [
            {
              url: "/cls/rx#",
              hash: "rxHash",
              img: "https://www.eyedock.com/images/menu_icons/vertex.svg",
              title: "Find Soft CLs",
              subtitle: "<i>Vertex first</i>, then find appropriate lenses",
            },
            {
              url: "/cls/rx#",
              hash: "clHash",
              img: "https://www.eyedock.com/images/menu_icons/vertex_not.svg",
              title: "Find Soft CLs",
              subtitle: "Find CLs with this Rx <i>(don't vertex first)</i>",
            },
          ],
        },
        {
          section: "Calculate Oblique Cross Cylinders",
          needs: ["rx"],
          items: [
            {
              url: "/calcs/oblique#",
              hash: "obliqueClHash",
              img: "https://www.eyedock.com/images/menu_icons/oblique_icon.svg",
              title: "Oblique Cross Cyls Calc",
              subtitle: "This Rx is the <i>contact lens Rx</i>",
            },
            {
              url: "/calcs/oblique#",
              hash: "clHash",
              img: "https://www.eyedock.com/images/crosses/eye ring.svg",
              title: "Oblique Cross Cyls Calc",
              subtitle: "This Rx is the <i>overrefraction</i>",
            },
          ],
        },
        {
          section: "Calculate Specialty Lenses",
          needs: ["rx", "k"],
          items: [
            {
              url: "/calcs/contact-lens#",
              hash: "rgpHash",
              img: "https://www.eyedock.com/images/menu_icons/scleral2_icon.svg",
              title: "Specialty Lens Calculations",
              subtitle: "Empirical GP &amp; Hybrid lens calcs",
            },
          ],
        },
        {
          section: "Patient Education",
          needs: ["rx"],
          items: [
            {
              url: "/refractive-error-simulator#",
              hash: "rxHash",
              img: "https://www.eyedock.com/images/menu_icons/eye_light_rays.svg",
              title: "Refractive Error Simulator",
              subtitle:
                "Demonstrate blur and how light focuses with myopia, hyperopia, astigmatism, and presbyopia. ",
            },
          ],
        },
        {
          section: "Other Optics Calculations",
          needs: ["rx"],
          items: [
            {
              url: "/calcs/meridian-power-calculator#",
              hash: "rxHash",
              img: "https://www.eyedock.com/images/menu_icons/meridian_calc_icon.svg",
              title: "Meridian Power Calcs",
              subtitle: "Find the power in any meridian",
            },
          ],
        },
      ],
    };
  },
  methods: {
    rowClick(obj) {
      // console.log(obj);
      let path = this.getDetailPath(obj);
      if (obj?.module == "CL" && this.rxObjects)
        path += "#" + this.rxHashOU(this.rxObjects);
      if (path) this.$router.push({ path: path });
    },
    newQ() {
      let val = this.qInput;
      // console.log(val);
      // val = val.replace(/\n+/g, ' ');
      // val = val.replace(/\r+/g, ' ');
      // val = val.replace(/\r+/g, ' ');
      val = val.replace(/\s\s+/g, " ");

      const encoded = encodeURIComponent(val, true);

      //   const numChars = encoded.length;
      this.$router.push({ path: "/search-all?q=" + encoded });
    },
    doPowerSearch() {
      let maybeRx = this.rxText.match(/.*[-+]\d.*/im);

      if (!maybeRx) return null;
      let url = "https://www.eyedock.com/bcse/api/parseRx2?rxText=";
      url += encodeURIComponent(this.rxText, true);

      this.loadingRx = true;
      ApiServices.genericApiCall(url)
        .then((response) => {
          let rxs = response.data?.rxs;
          let rxAlternatives = response.data?.rxAlternatives;
          if (rxs && rxs.length) {
            ["od", "os"].forEach((eye) => {
              let rxObj = this.setRxObjectParameters(null, rxs[0][eye]);
              if (rxObj && !this.isPlano(rxObj)) this.rxObjects[eye] = rxObj;
            });
            if (rxAlternatives.length > 1) this.rxAlternatives = rxAlternatives;
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loadingRx = false;
        });
    },
    doKeratoSearch() {
      let maybeK = this.kText.match(/.*[3-7][0-9].*/im);
      if (!maybeK) return null;
      let url = "https://www.eyedock.com/bcse/api/parseK?kText=";
      url += encodeURIComponent(this.kText, true);

      this.loadingK = true;
      ApiServices.genericApiCall(url)
        .then((response) => {
          let ks = response.data?.ks;
          let kAlternatives = response.data?.kAlternatives;
          if (ks) {
            ["od", "os"].forEach((eye) => {
              // console.log(ks[0][eye]);
              let kObj = null;
              if (ks?.[0]?.[eye])
                kObj = this.setKeratometryParameters(null, ks[0][eye]);
              if (kObj && this.isValidFullK(kObj)) this.kObjects[eye] = kObj;
              if (kAlternatives && kAlternatives.length > 1)
                this.kAlternatives = kAlternatives;
            });
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loadingK = false;
        });
    },
    searchAll() {
      // console.log("search all");
      if (!this.q) return;
      // console.log("search all", this.q);
      this.loading = true;
      ApiServices.genericApiCall(
        "searchAll2?q=" + encodeURIComponent(this.q) + "&limit=6"
      )
        .then((response) => {
          this.items = response.data.items;
          this.groups = response.data.meta.groups;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    showQueryIcon: function () {
      let q = this.q;
      return q && q.length > 150;
    },
    hasRefraction: function () {
      return this.rxObjects && (this.rxObjects.od || this.rxObjects.os);
    },
    hasKeratometry: function () {
      return this.kObjects && (this.kObjects.od || this.kObjects.os);
    },
    hasCalculatables: function () {
      return this.hasRefraction || this.hasKeratometry;
    },
    rxActions: function () {
      if (!this.hasCalculatables) return null;
      //   console.log("rxActions");
      let rxHash = this.rxHashOU(this.rxObjects);
      let hashNoVertex = rxHash.replace(
        /([^\0]*)(&vertex=[.0-9]+)([^\0]*)/gim,
        "$1$3"
      );
      let kHash = this.keratometryHashOU(this.kObjects);
      let rgpHashArr = [];
      if (rxHash) rgpHashArr.push(rxHash);
      if (kHash) rgpHashArr.push(kHash);

      // let clHash = hashNoVertex + "&vertex=0";
      // let obliqueClHash = clHash.replaceAll("refraction", "cl");

      let hashes = {};
      if (rxHash) {
        hashes.rxHash = rxHash;
        hashes.clHash = hashNoVertex + "&vertex=0";
        hashes.obliqueClHash = hashes.clHash.replaceAll("refraction", "cl");
      }
      if (rgpHashArr && rgpHashArr.length)
        hashes.rgpHash = rgpHashArr.join("&");

      return hashes;
    },
    headersForSize: function () {
      return this.$vuetify.breakpoint.smAndUp ? this.headers : this.unoHeader;
    },

    uriEncoded: function () {
      if (!this.q) return null;
      return encodeURIComponent(this.q);
    },
    itemsToShow: function () {
      if (!this.items || !this.items.length) return null;
      let isSubscribed = this.isSubscribed;
      // if (this.isSubscribed) return this.items;
      let rtn = [];
      let freeModules = this.freeModules;
      let namesForModule = {}; // to eliminate duplicates
      this.items.forEach((row) => {
        const n = row.name;
        const m = row.module;
        if (!namesForModule[m]) namesForModule[m] = [];
        const isNew = !namesForModule[m].includes(n);
        const canInclude = isSubscribed || freeModules.includes(m);
        // console.log(isNew, canInclude);
        if (isNew && canInclude) rtn.push(row);
        namesForModule[m].push(n);
      });
      // console.log(namesForModule);
      return rtn;
    },
  },
  mounted() {
    let q = this.$route.query.q;
    // console.log(q);
    let rxText = this.$route.query.rx ?? null;
    let kText = this.$route.query.k ?? null;

    if (q) {
      q = decodeURIComponent(q);
      this.q = q;
      if (!rxText) rxText = q;
      if (!kText) kText = q;
      this.qInput = q;
      if (q.length > 2) {
        // if a good query
        this.searchAll();
        // this.qInput = q;
      } else {
        // if query is too short
        this.tooFewChars = true;
        this.editMode = true;
      }
    }
    if (rxText) {
      this.rxText = rxText;
      this.doPowerSearch();
    }
    if (kText) {
      this.kText = kText;
      this.doKeratoSearch();
    }

    if (!q && !rxText && !kText) {
      // if no query provided
      this.editMode = true;
    }
  },
  metaInfo() {
    let txt = this.q ? this.q.substring(0, 10) + "..." : "";
    return {
      title: "EyeDock/Search | " + txt,
    };
  },
};
</script>

<style scoped>
.mobileImageDiv {
  width: 40px;
}
.resultItemCard {
  background-color: rgb(249 253 255);
  border: 1px solid #c2d0da;
}
.daTable >>> .v-row-group__header {
  background-color: rgb(239 247 250);
  border: 1px solid #b5c9d6;
  border-radius: 2px;
  padding: 2px;
}
.daTable >>> .v-row-group__header td {
  border: none !important;
}
.listItem {
  border: 1px solid #d8d8d8;
  background-color: #f5ffe7;
}
.v-list-item__subtitle {
  white-space: normal;
  overflow-wrap: break-word;
}
</style>
